import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil, ImageUp, Trash } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import {
  getShowEstablishments,
  updateEstablishmentsData,
} from "@/Api/establishments";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { getCet } from "@/Api/cep";
import { getSelectPaymentForms } from "@/Api/paymentForms";
import { getSelectUnities } from "@/Api/unities";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { FormSteps, useStepControl } from "@/components/Form/Steps";
import { getSelectSaleTypes } from "@/Api/saleTypes";

interface Method {
  id: string;
  name: string;
}

interface Unity {
  id: string;
  name: string;
}

const step1Schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido").max(255),
  image: z.any(),
});

const step2Schema = z.object({
  street: z.string().min(1, "*requerido").max(255),
  number: z.string().min(1, "*requerido").max(255),
  neighborhood: z.string().min(1, "*requerido").max(255),
  complement: z.string().max(255).optional(),
  state: z.string().min(1, "*requerido").max(255),
  city: z.string().min(1, "*requerido").max(255),
  zip_code: z.string().min(1, "*requerido").max(255),
});

const step3Schema = z.object({
  delivery_methods: z.any().optional(),
  payment_methods: z.any().optional(),
});

const step4Schema = z.object({
  unities: z.any().optional(),
});

const schemas = {
  1: step1Schema,
  2: step2Schema,
  3: step3Schema,
  4: step4Schema,
};

const schema = z.object({
  ...step1Schema.shape,
  ...step2Schema.shape,
  ...step3Schema.shape,
  ...step4Schema.shape,
});

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (response: any) => void;
  uuid: string;
}) => {
  const [deliveryMethods, setDeliveryMethods] = useState<Method[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<Method[]>([]);
  const [unities, setUnities] = useState<Unity[]>([]);
  const stepControls = useStepControl();
  const [isOpen, setIsOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const [establishmentRes, deliveryRes, paymentRes, unitiesRes] =
          await Promise.all([
            getShowEstablishments(uuid),
            getSelectSaleTypes(),
            getSelectPaymentForms(),
            getSelectUnities(),
          ]);

        if (establishmentRes.ok) {
          const data = establishmentRes.data?.response;
          form.setValue("name", data?.name || "");
          form.setValue("street", data?.address?.street || "");
          form.setValue("number", data?.address?.number || "");
          form.setValue("neighborhood", data?.address?.neighborhood || "");
          form.setValue("complement", data?.address?.complement || "");
          form.setValue("state", data?.address?.state || "");
          form.setValue("city", data?.address?.city || "");
          form.setValue("zip_code", data?.address?.zip_code || "");
          form.setValue(
            "delivery_methods",
            data?.delivery_methods?.map((m: any) => m.id) || []
          );
          form.setValue(
            "payment_methods",
            data?.payment_forms?.map((m: any) => m.id) || []
          );
          form.setValue("unities", data?.unities?.map((u: any) => u.id) || []);
          setImagePreview(data?.image || null);
        }

        if (deliveryRes.ok)
          setDeliveryMethods(deliveryRes.data?.response || []);
        if (paymentRes.ok) setPaymentMethods(paymentRes.data?.response || []);
        if (unitiesRes.ok) setUnities(unitiesRes.data?.response || []);
      } catch (error) {
        console.error("Error loading data:", error);
        toast({
          description: "Erro ao carregar dados",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isOpen, uuid, form, toast]);

  const handleImageChange = (file: File | null) => {
    form.setValue("image", file);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
    }
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const cep = e.target.value.replace(/\D/g, "");

    if (cep.length === 8) {
      try {
        const { data } = await getCet(cep);
        if (!data.erro) {
          form.setValue("street", data.logradouro);
          form.setValue("neighborhood", data.bairro);
          form.setValue("city", data.localidade);
          form.setValue("state", data.uf);
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
      }
    }
  };

  const handleNextStep = async () => {
    const currentSchema = schemas[stepControls.step as keyof typeof schemas];
    const currentStepData = form.getValues();

    try {
      await currentSchema.parseAsync(currentStepData);
      stepControls.setStep(stepControls.step + 1);
    } catch (error) {
      form.trigger();
    }
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const payload = {
      ...data,
      image: data.image instanceof File ? data.image : null,
    };

    const { data: apiResponse, ok } = await updateEstablishmentsData(
      uuid,
      payload
    );
    if (!ok) {
      setFormState("error");
      setTimeout(() => {
        setFormState("default");
        setIsOpen(false);
      }, 3000);
      return;
    }

    updateFront(apiResponse?.response);
    toast({
      description: `Estabelecimento atualizado com sucesso!`,
      action: <Check color="#1edc2b" />,
    });

    setFormState("default");
    stepControls.setStep(1);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={() => setIsOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Estabelecimento
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center items-center h-[400px]">
            <LoaderSpin />
          </div>
        ) : (
          <FormSteps controls={stepControls}>
            <FormSteps.Header>
              <FormSteps.Indicator step={1}>
                Informações Básicas
              </FormSteps.Indicator>
              <FormSteps.Indicator step={2}>Endereço</FormSteps.Indicator>
              <FormSteps.Indicator step={3}>Métodos</FormSteps.Indicator>
              <FormSteps.Indicator step={4}>Unidades</FormSteps.Indicator>
            </FormSteps.Header>

            <ScrollArea className="h-[calc(90vh-15rem)] sm:h-[calc(90vh-12rem)] pr-4">
              <Form
                onSubmit={form.handleSubmit(formSubmit)}
                {...form}
                className="space-y-3 p-2"
              >
                <FormSteps.Content step={1}>
                  <Form.Row>
                    <Form.Field name="name">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Nome <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input {...field} placeholder="Insira um nome" />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <div className="mt-5">
                    <label className="text-1xl font-medium text-gray-900 dark:text-zinc-300">
                      Foto:
                    </label>
                    <div className="flex justify-center itens-center rounded-md border border-dashed dark:border-gray-500 border-gray-900/25 px-2 py-3 mt-2">
                      <div className="flex flex-col items-center gap-4">
                        {imagePreview ? (
                          <img
                            className="rounded-md aspect-square w-40 object-cover"
                            src={imagePreview}
                            alt="Logo preview"
                          />
                        ) : (
                          <ImageUp className="mx-auto size-12 text-gray-300 mt-2" />
                        )}
                        <div className="flex justify-center items-center gap-5 text-sm/6 text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer rounded-md bg-transparent font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-700"
                          >
                            <span className="flex items-center gap-1">
                              {imagePreview && <ImageUp className="size-5" />}
                              Procurar imagem
                            </span>
                            <input
                              id="file-upload"
                              type="file"
                              className="sr-only"
                              accept="image/*"
                              onChange={(e) =>
                                handleImageChange(e.target.files?.[0] || null)
                              }
                            />
                          </label>
                          {imagePreview && (
                            <Button
                              type="button"
                              className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                              onClick={() => handleImageChange(null)}
                            >
                              <Trash className="size-5" />
                              Remover
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </FormSteps.Content>

                <FormSteps.Content step={2}>
                  <Form.Row>
                    <Form.Field name="zip_code">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            CEP <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              placeholder="Insira o CEP"
                              onChange={(e) => {
                                field.onChange(e);
                                handleCepChange(e);
                              }}
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <Form.Row>
                    <Form.Field name="street">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Rua <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input {...field} placeholder="Insira a rua" />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>

                    <Form.Field name="number">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Número <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input {...field} placeholder="Insira o número" />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <Form.Row>
                    <Form.Field name="neighborhood">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Bairro <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input {...field} placeholder="Insira o bairro" />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>

                    <Form.Field name="complement">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Complemento <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              placeholder="Insira o complemento"
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <Form.Row>
                    <Form.Field name="state">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Estado <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input {...field} placeholder="Insira o estado" />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>

                    <Form.Field name="city">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Cidade <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input {...field} placeholder="Insira a cidade" />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                </FormSteps.Content>

                <FormSteps.Content step={3}>
                  <Form.Row>
                    <Form.Field name="delivery_methods">
                      {({ field }) => (
                        <Form.Item className="w-full">
                          <Form.Label>
                            Métodos de Entrega <Form.Message />
                          </Form.Label>
                          <div className="flex flex-col gap-2">
                            {deliveryMethods.map((method) => (
                              <div
                                key={method.id}
                                className="flex items-center gap-2"
                              >
                                <Checkbox
                                  id={`delivery-${method.id}`}
                                  checked={field.value?.includes(method.id)}
                                  onCheckedChange={(checked) => {
                                    const newValue = checked
                                      ? [...(field.value || []), method.id]
                                      : field.value?.filter(
                                          (id) => id !== method.id
                                        ) || [];
                                    field.onChange(newValue);
                                  }}
                                />
                                <label
                                  htmlFor={`delivery-${method.id}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {method.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <Form.Row>
                    <Form.Field name="payment_methods">
                      {({ field }) => (
                        <Form.Item className="w-full">
                          <Form.Label>
                            Métodos de Pagamento <Form.Message />
                          </Form.Label>
                          <div className="flex flex-col gap-2">
                            {paymentMethods.map((method) => (
                              <div
                                key={method.id}
                                className="flex items-center gap-2"
                              >
                                <Checkbox
                                  id={`payment-${method.id}`}
                                  checked={field.value?.includes(method.id)}
                                  onCheckedChange={(checked) => {
                                    const newValue = checked
                                      ? [...(field.value || []), method.id]
                                      : field.value?.filter(
                                          (id) => id !== method.id
                                        ) || [];
                                    field.onChange(newValue);
                                  }}
                                />
                                <label
                                  htmlFor={`payment-${method.id}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {method.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                </FormSteps.Content>

                <FormSteps.Content step={4}>
                  <Form.Row>
                    <Form.Field name="unities">
                      {({ field }) => (
                        <Form.Item className="w-full">
                          <Form.Label>
                            Unidades <Form.Message />
                          </Form.Label>
                          <div className="flex flex-col gap-2">
                            {unities.map((unity) => (
                              <div
                                key={unity.id}
                                className="flex items-center gap-2"
                              >
                                <Checkbox
                                  id={`unity-${unity.id}`}
                                  checked={field.value?.includes(unity.id)}
                                  onCheckedChange={(checked) => {
                                    const newValue = checked
                                      ? [...(field.value || []), unity.id]
                                      : field.value?.filter(
                                          (id) => id !== unity.id
                                        ) || [];
                                    field.onChange(newValue);
                                  }}
                                />
                                <label
                                  htmlFor={`unity-${unity.id}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {unity.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                </FormSteps.Content>

                <DialogFooter className="sticky bottom-0 bg-background pt-4">
                  <div className="flex w-full gap-2">
                    {stepControls.step > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          stepControls.setStep(stepControls.step - 1)
                        }
                      >
                        Voltar
                      </Button>
                    )}

                    {stepControls.step < 4 && (
                      <Button
                        type="button"
                        className={cn("flex-1", colors.bg_primary)}
                        onClick={handleNextStep}
                      >
                        Próximo
                      </Button>
                    )}

                    {stepControls.step === 4 && (
                      <Button
                        type="submit"
                        className={cn("flex-1", colors.bg_primary)}
                        disabled={formState !== "default"}
                      >
                        {formState === "loading" && (
                          <>
                            Atualizando <LoaderSpin />
                          </>
                        )}
                        {formState === "default" && "Atualizar"}
                        {formState === "error" && "Erro ao atualizar"}
                      </Button>
                    )}
                  </div>
                </DialogFooter>
              </Form>
            </ScrollArea>
          </FormSteps>
        )}
      </DialogContent>
    </Dialog>
  );
};
